// Tracks "active" wall-clock time, excluding stretches where the user didn't
// interact for more than IDLE_THRESHOLD_MS or the tab was hidden. Consumers
// (pageTimeTracker, session_end) call getActiveMsSince(from) to get the active
// subset of an interval instead of the naive (now - from) wall clock.
//
// Model: every input signal (mousemove/keydown/click/scroll/touch) opens or
// extends a "live" interval that expires IDLE_THRESHOLD_MS after the last
// signal. Intervals are the union of [signal_i, signal_i + THRESHOLD], so the
// first 5 min after an input always count as active even if the user sits
// still — beyond that, they're excluded. Tab visibility going hidden closes
// the open interval immediately (no threshold grace).

const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
const SIGNAL_THROTTLE_MS = 500;
const PRUNE_OLDER_THAN_MS = 24 * 60 * 60 * 1000;

// Closed, non-overlapping, sorted intervals of active time.
let intervals = [];
// Currently-open interval (null if no recent signal / tab hidden).
let openStart = null;
let openLastSignal = null;
let lastThrottleAt = 0;
let listenersAttached = false;

function _closeOpen(nowTs) {
  if (openStart === null) return;
  const end = Math.min(openLastSignal + IDLE_THRESHOLD_MS, nowTs);
  if (end > openStart) intervals.push({ start: openStart, end });
  openStart = null;
  openLastSignal = null;
  _prune();
}

function _prune() {
  const cutoff = Date.now() - PRUNE_OLDER_THAN_MS;
  if (intervals.length && intervals[0].end < cutoff) {
    intervals = intervals.filter((iv) => iv.end >= cutoff);
  }
}

function _emitSignal(nowTs) {
  if (nowTs - lastThrottleAt < SIGNAL_THROTTLE_MS) return;
  lastThrottleAt = nowTs;
  if (openStart === null) {
    openStart = nowTs;
    openLastSignal = nowTs;
    return;
  }
  if (nowTs - openLastSignal > IDLE_THRESHOLD_MS) {
    intervals.push({ start: openStart, end: openLastSignal + IDLE_THRESHOLD_MS });
    _prune();
    openStart = nowTs;
  }
  openLastSignal = nowTs;
}

export function markActive() {
  _emitSignal(Date.now());
}

export function getActiveMsSince(fromTs, endTs = Date.now()) {
  if (!fromTs || endTs <= fromTs) return 0;
  let total = 0;
  for (const iv of intervals) {
    if (iv.end <= fromTs) continue;
    if (iv.start >= endTs) break;
    const lo = Math.max(fromTs, iv.start);
    const hi = Math.min(endTs, iv.end);
    if (hi > lo) total += hi - lo;
  }
  if (openStart !== null) {
    const openEnd = Math.min(openLastSignal + IDLE_THRESHOLD_MS, endTs);
    const lo = Math.max(fromTs, openStart);
    if (openEnd > lo) total += openEnd - lo;
  }
  return total;
}

export function startIdleTracking() {
  if (listenersAttached || typeof window === 'undefined') return;
  listenersAttached = true;
  const onSignal = () => _emitSignal(Date.now());
  window.addEventListener('mousemove', onSignal, { passive: true });
  window.addEventListener('keydown', onSignal, { passive: true });
  window.addEventListener('click', onSignal, { passive: true });
  window.addEventListener('scroll', onSignal, { capture: true, passive: true });
  window.addEventListener('touchstart', onSignal, { passive: true });
  document.addEventListener('visibilitychange', () => {
    const now = Date.now();
    if (document.visibilityState === 'hidden') {
      _closeOpen(now);
    } else {
      _emitSignal(now);
    }
  });
  window.addEventListener('beforeunload', () => _closeOpen(Date.now()));
  _emitSignal(Date.now());
}

export function _resetForTests() {
  intervals = [];
  openStart = null;
  openLastSignal = null;
  lastThrottleAt = 0;
  listenersAttached = false;
}

export const IDLE_THRESHOLD_MS_EXPORT = IDLE_THRESHOLD_MS;
